import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Paper,
  Title,
  Text,
  Group,
  Stack,
  Badge,
  Button,
  Collapse,
  UnstyledButton,
  ThemeIcon,
  Skeleton,
  SimpleGrid,
  Tooltip,
  Box,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconAlertCircle,
  IconInfoCircle,
  IconTrendingUp,
  IconTrendingDown,
  IconChevronDown,
  IconChevronRight,
  IconBulb,
  IconPlayerPlay,
  IconSparkles,
  IconRefresh,
} from '@tabler/icons-react';
import { fetchOne } from '@/hooks/api/helpers';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InsightAlert {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  detail: string;
  metric_value?: string;
  action_label?: string;
  action_path?: string;
}

export interface InsightTrend {
  id: string;
  label: string;
  current_value: number;
  prior_value: number;
  change_pct: number;
  direction: 'up' | 'down' | 'flat';
  format: 'currency' | 'number' | 'percent';
}

export interface InsightSuggestion {
  id: string;
  title: string;
  description: string;
  impact: string;
  action_label: string;
  action_path?: string;
  priority: 'high' | 'medium' | 'low';
}

export interface AccountingInsightsData {
  alerts: InsightAlert[];
  trends: InsightTrend[];
  suggestions: InsightSuggestion[];
  generated_at: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SEVERITY_CONFIG = {
  critical: { color: 'red', icon: IconAlertTriangle, label: 'Critical' },
  warning: { color: 'yellow', icon: IconAlertCircle, label: 'Warning' },
  info: { color: 'blue', icon: IconInfoCircle, label: 'Info' },
} as const;

const PRIORITY_COLOR = {
  high: 'red',
  medium: 'orange',
  low: 'blue',
} as const;

function formatTrendValue(value: number, format: InsightTrend['format']): string {
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    case 'percent':
      return `${value.toFixed(1)}%`;
    case 'number':
    default:
      return new Intl.NumberFormat('en-US').format(value);
  }
}

// ---------------------------------------------------------------------------
// Section Toggle
// ---------------------------------------------------------------------------

function SectionHeader({
  title,
  icon,
  count,
  expanded,
  onToggle,
  badgeColor,
}: {
  title: string;
  icon: React.ReactNode;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  badgeColor?: string;
}) {
  return (
    <UnstyledButton onClick={onToggle} w="100%">
      <Group justify="space-between" py="xs">
        <Group gap="sm">
          {icon}
          <Title order={5}>{title}</Title>
          {count > 0 && (
            <Badge color={badgeColor || 'gray'} variant="light" size="sm">
              {count}
            </Badge>
          )}
        </Group>
        {expanded ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
      </Group>
    </UnstyledButton>
  );
}

// ---------------------------------------------------------------------------
// Alert Card
// ---------------------------------------------------------------------------

function AlertCard({ alert }: { alert: InsightAlert }) {
  const navigate = useNavigate();
  const config = SEVERITY_CONFIG[alert.severity];
  const Icon = config.icon;

  return (
    <Paper
      p="md"
      withBorder
      style={{
        borderLeft: `4px solid var(--mantine-color-${config.color}-5)`,
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="sm" align="flex-start" wrap="nowrap" style={{ flex: 1 }}>
          <ThemeIcon color={config.color} variant="light" size="lg" radius="xl" mt={2}>
            <Icon size={18} />
          </ThemeIcon>
          <Stack gap={4} style={{ flex: 1 }}>
            <Group gap="xs">
              <Text fw={600} size="sm">
                {alert.title}
              </Text>
              <Badge color={config.color} variant="light" size="xs">
                {config.label}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed" lineClamp={2}>
              {alert.detail}
            </Text>
          </Stack>
        </Group>
        <Stack gap="xs" align="flex-end" style={{ flexShrink: 0 }}>
          {alert.metric_value && (
            <Text fw={700} size="lg" c={config.color}>
              {alert.metric_value}
            </Text>
          )}
          {alert.action_label && alert.action_path && (
            <Button
              variant="light"
              color={config.color}
              size="xs"
              onClick={() => navigate(alert.action_path!)}
            >
              {alert.action_label}
            </Button>
          )}
        </Stack>
      </Group>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Trend Card
// ---------------------------------------------------------------------------

function TrendCard({ trend }: { trend: InsightTrend }) {
  const isPositive = trend.direction === 'up';
  const isFlat = trend.direction === 'flat';
  const trendColor = isFlat ? 'gray' : isPositive ? 'teal' : 'red';

  return (
    <Paper p="md" withBorder>
      <Stack gap={4}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.03em' }}>
          {trend.label}
        </Text>
        <Text fw={700} size="xl">
          {formatTrendValue(trend.current_value, trend.format)}
        </Text>
        <Group gap={6}>
          <Tooltip
            label={`Previous: ${formatTrendValue(trend.prior_value, trend.format)}`}
            withArrow
          >
            <Badge
              variant="light"
              color={trendColor}
              size="sm"
              leftSection={
                isFlat ? null : isPositive ? (
                  <IconTrendingUp size={12} />
                ) : (
                  <IconTrendingDown size={12} />
                )
              }
            >
              {isFlat
                ? 'No change'
                : `${isPositive ? '+' : ''}${trend.change_pct.toFixed(1)}%`}
            </Badge>
          </Tooltip>
          <Text size="xs" c="dimmed">
            vs prior period
          </Text>
        </Group>
      </Stack>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Suggestion Card
// ---------------------------------------------------------------------------

function SuggestionCard({ suggestion }: { suggestion: InsightSuggestion }) {
  const navigate = useNavigate();
  const priorityColor = PRIORITY_COLOR[suggestion.priority];

  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="sm" align="flex-start" wrap="nowrap" style={{ flex: 1 }}>
          <ThemeIcon color="grape" variant="light" size="lg" radius="xl" mt={2}>
            <IconBulb size={18} />
          </ThemeIcon>
          <Stack gap={4} style={{ flex: 1 }}>
            <Group gap="xs">
              <Text fw={600} size="sm">
                {suggestion.title}
              </Text>
              <Badge color={priorityColor} variant="dot" size="xs">
                {suggestion.priority}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed" lineClamp={2}>
              {suggestion.description}
            </Text>
            {suggestion.impact && (
              <Text size="xs" c="teal" fw={500}>
                Impact: {suggestion.impact}
              </Text>
            )}
          </Stack>
        </Group>
        {suggestion.action_path && (
          <Button
            variant="light"
            color="grape"
            size="xs"
            leftSection={<IconPlayerPlay size={14} />}
            onClick={() => navigate(suggestion.action_path!)}
            style={{ flexShrink: 0 }}
          >
            {suggestion.action_label || 'Do it'}
          </Button>
        )}
      </Group>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function AIInsights() {
  const [alertsOpen, setAlertsOpen] = useState(true);
  const [trendsOpen, setTrendsOpen] = useState(true);
  const [suggestionsOpen, setSuggestionsOpen] = useState(true);

  const {
    data: insights,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['accounting-insights'],
    queryFn: () => fetchOne<AccountingInsightsData>('/accounting/insights'),
    refetchInterval: 5 * 60 * 1000,
    retry: 1,
  });

  // Loading state
  if (isLoading) {
    return (
      <Paper p="md" withBorder>
        <Group gap="sm" mb="md">
          <IconSparkles size={20} />
          <Title order={4}>AI Insights</Title>
        </Group>
        <Stack gap="sm">
          <Skeleton height={60} radius="md" />
          <Skeleton height={60} radius="md" />
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="sm">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height={90} radius="md" />
            ))}
          </SimpleGrid>
        </Stack>
      </Paper>
    );
  }

  // Error state -- fail silently with a minimal notice so the rest of the
  // dashboard remains usable
  if (error || !insights) {
    return (
      <Paper p="md" withBorder>
        <Group justify="space-between">
          <Group gap="sm">
            <IconSparkles size={20} opacity={0.5} />
            <Text size="sm" c="dimmed">
              AI Insights unavailable
            </Text>
          </Group>
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconRefresh size={14} />}
            onClick={() => refetch()}
          >
            Retry
          </Button>
        </Group>
      </Paper>
    );
  }

  const { alerts, trends, suggestions } = insights;
  const criticalCount = alerts.filter((a) => a.severity === 'critical').length;

  return (
    <Paper p="md" withBorder>
      {/* Header */}
      <Group justify="space-between" mb="md">
        <Group gap="sm">
          <ThemeIcon color="grape" variant="light" size="md" radius="xl">
            <IconSparkles size={16} />
          </ThemeIcon>
          <Title order={4}>AI Insights</Title>
          {criticalCount > 0 && (
            <Badge color="red" variant="filled" size="sm">
              {criticalCount} critical
            </Badge>
          )}
        </Group>
        <Group gap="xs">
          {insights.generated_at && (
            <Text size="xs" c="dimmed">
              Updated {new Date(insights.generated_at).toLocaleTimeString()}
            </Text>
          )}
          <Tooltip label="Refresh insights" withArrow>
            <Button
              variant="subtle"
              size="xs"
              px={6}
              loading={isFetching}
              onClick={() => refetch()}
              aria-label="Refresh AI insights"
            >
              <IconRefresh size={14} />
            </Button>
          </Tooltip>
        </Group>
      </Group>

      <Stack gap="sm">
        {/* ---- Alerts Section ---- */}
        {alerts.length > 0 && (
          <Box>
            <SectionHeader
              title="Alerts"
              icon={<IconAlertTriangle size={18} color="var(--mantine-color-orange-6)" />}
              count={alerts.length}
              expanded={alertsOpen}
              onToggle={() => setAlertsOpen((v) => !v)}
              badgeColor={criticalCount > 0 ? 'red' : 'orange'}
            />
            <Collapse in={alertsOpen}>
              <Stack gap="xs">
                {alerts.map((alert) => (
                  <AlertCard key={alert.id} alert={alert} />
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}

        {/* ---- Trends Section ---- */}
        {trends.length > 0 && (
          <Box>
            <SectionHeader
              title="Trends"
              icon={<IconTrendingUp size={18} color="var(--mantine-color-teal-6)" />}
              count={trends.length}
              expanded={trendsOpen}
              onToggle={() => setTrendsOpen((v) => !v)}
              badgeColor="teal"
            />
            <Collapse in={trendsOpen}>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="sm">
                {trends.map((trend) => (
                  <TrendCard key={trend.id} trend={trend} />
                ))}
              </SimpleGrid>
            </Collapse>
          </Box>
        )}

        {/* ---- Suggestions Section ---- */}
        {suggestions.length > 0 && (
          <Box>
            <SectionHeader
              title="Suggestions"
              icon={<IconBulb size={18} color="var(--mantine-color-grape-6)" />}
              count={suggestions.length}
              expanded={suggestionsOpen}
              onToggle={() => setSuggestionsOpen((v) => !v)}
              badgeColor="grape"
            />
            <Collapse in={suggestionsOpen}>
              <Stack gap="xs">
                {suggestions.map((suggestion) => (
                  <SuggestionCard key={suggestion.id} suggestion={suggestion} />
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}

        {/* ---- Empty state ---- */}
        {alerts.length === 0 && trends.length === 0 && suggestions.length === 0 && (
          <Text size="sm" c="dimmed" ta="center" py="lg">
            No insights available at this time. Check back later.
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
